/**
 * EMS Search Modal - Doofinder-style search widget
 * A fullscreen search modal with recent searches, popular searches, and recommended products
 */
(function($) {
  'use strict';

  // Search history management using localStorage (per site and module)
  const SearchHistory = {
    STORAGE_KEY: 'ems_search_history',
    MAX_ITEMS: 10,

    getKey(siteId, moduleId) {
      return `${this.STORAGE_KEY}_${siteId}_${moduleId}`;
    },

    get(siteId, moduleId) {
      try {
        const data = localStorage.getItem(this.getKey(siteId, moduleId));
        return data ? JSON.parse(data) : [];
      } catch (e) {
        return [];
      }
    },

    add(siteId, moduleId, query) {
      if (!query || query.length < 2) return;

      let history = this.get(siteId, moduleId);
      // Remove if exists (to move to front)
      history = history.filter((q) => q.toLowerCase() !== query.toLowerCase());
      // Add to front
      history.unshift(query);
      // Keep max items
      history = history.slice(0, this.MAX_ITEMS);

      try {
        localStorage.setItem(this.getKey(siteId, moduleId), JSON.stringify(history));
      } catch (e) {
        // localStorage might be full or disabled
      }
    },

    remove(siteId, moduleId, query) {
      let history = this.get(siteId, moduleId);
      history = history.filter((q) => q.toLowerCase() !== query.toLowerCase());

      try {
        localStorage.setItem(this.getKey(siteId, moduleId), JSON.stringify(history));
      } catch (e) {}
    },

    clear(siteId, moduleId) {
      try {
        localStorage.removeItem(this.getKey(siteId, moduleId));
      } catch (e) {}
    },
  };

  $.EmsSearchModal = function(options) {
    const defaults = {
      search_url: '',
      modal_data_url: '',
      track_query_url: '',
      module_id: 'eco',
      site_id: '',
      site_url: '',
      lang: 'fr',
      debounce_ms: 200,
      min_chars: 2,
      labels: {
        recent_searches: 'Dernieres recherches',
        popular_searches: 'Recherches populaires',
        recommended: 'Produits recommandes',
        clear_all: 'Supprimer tout',
        search_placeholder: 'Chercher...',
        no_results: 'Aucun resultat',
        close: 'Fermer',
        price: 'Prix',
        category: 'Categorie',
        clear_filters: 'Effacer les filtres',
      },
    };

    options = $.extend(true, defaults, options);

    // Validate required options
    if (!options.search_url || !options.site_id) {
      console.error('EmsSearchModal: search_url and site_id are required');
      return;
    }

    let modalElement = null;
    let searchInput = null;
    let debounceTimer = null;
    let currentRequest = null;
    let modalData = null;
    let modalDataLoaded = false;
    let isOpen = false;

    // Infinite scroll state
    let currentPage = 1;
    let totalPages = 1;
    let isLoadingMore = false;
    let currentQuery = '';
    let infiniteScrollObserver = null;

    // Price filter state (for eco module)
    let priceFilterMin = null;
    let priceFilterMax = null;
    let priceRangeMin = null; // Min from facet stats (initial search)
    let priceRangeMax = null; // Max from facet stats (initial search)
    let priceRangeInitialized = false; // Track if we've set the initial range
    let priceFilterDebounce = null;

    // Category filter state (all modules) - supports multiple categories
    let selectedCategoryIds = [];
    let categoryFacets = []; // Categories from search results

    // Initialize modal
    const init = () => {
      require('./sass/modal.bootstrap_v4.scss');
      const template = require('./templates/modal.bootstrap_v4.twig');

      // Render template and append to body
      const html = template(options);
      $('body').append(html);

      modalElement = $('#ems-search-modal-overlay');
      searchInput = $('#ems-search-modal-input');

      bindEvents();
    };

    // Fetch initial modal data from server
    const fetchModalData = () => {
      $.ajax({
        url: options.modal_data_url,
        data: {lang: options.lang},
        dataType: 'json',
        success: (data) => {
          modalData = data;
          renderInitialContent();
        },
        error: (err) => {
          console.warn('EmsSearchModal: Failed to fetch modal data', err);
        },
      });
    };

    // Render initial content (before user types)
    const renderInitialContent = () => {
      renderRecentSearches();
      renderPopularSearches();
      renderRecommendedProducts();
    };

    // Render recent searches from localStorage
    const renderRecentSearches = () => {
      const container = $('#ems-search-recent');
      const history = SearchHistory.get(options.site_id, options.module_id);

      if (!history.length) {
        container.hide();
        return;
      }

      let html = `<div class="ems-search-section-header">
        <span class="section-title">${options.labels.recent_searches}</span>
        <a href="#" class="clear-history">${options.labels.clear_all}</a>
      </div>
      <div class="ems-search-tags">`;

      history.forEach((query) => {
        html += `<span class="ems-search-tag" data-query="${escapeHtml(query)}">
          ${escapeHtml(query)}
          <span class="remove-tag" data-query="${escapeHtml(query)}">&times;</span>
        </span>`;
      });

      html += '</div>';
      container.html(html).show();
    };

    // Render popular searches from server
    const renderPopularSearches = () => {
      const container = $('#ems-search-popular');

      if (!modalData?.popularSearches?.length) {
        container.hide();
        return;
      }

      let html = `<div class="ems-search-section-header">
        <span class="section-title">${options.labels.popular_searches}</span>
      </div>
      <ul class="ems-search-popular-list">`;

      modalData.popularSearches.forEach((item) => {
        html += `<li><a href="#" class="popular-search-link" data-query="${escapeHtml(item.query)}">${escapeHtml(item.query)}</a></li>`;
      });

      html += '</ul>';
      container.html(html).show();
    };

    // Render recommended items (products, blogposts, or pages)
    const renderRecommendedProducts = () => {
      const container = $('#ems-search-recommended');
      const products = modalData?.recommended?.products;
      const blogposts = modalData?.recommended?.blogposts;
      const pages = modalData?.recommended?.pages;

      if (!products?.length && !blogposts?.length && !pages?.length) {
        container.hide();
        return;
      }

      let html = `<div class="ems-search-section-header">
        <span class="section-title">${options.labels.recommended}</span>
      </div>
      <div class="ems-search-carousel">
        <div class="carousel-inner">`;

      if (products?.length) {
        products.forEach((product) => {
          html += renderProductCard(product);
        });
      } else if (blogposts?.length) {
        blogposts.forEach((blogpost) => {
          html += renderBlogpostCard(blogpost);
        });
      } else if (pages?.length) {
        pages.forEach((page) => {
          html += renderPageCard(page);
        });
      }

      html += '</div></div>';
      container.html(html).show();
    };

    // Render a single product card
    const renderProductCard = (product) => {
      let html = `<div class="ems-product-card">
        <a href="${escapeHtml(product.url)}" class="product-link">`;

      if (product.image) {
        html += `<div class="product-image">
          <img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" loading="lazy">`;

        if (product.hasReduction && product.reductionType) {
          html += `<span class="discount-badge">${escapeHtml(product.reductionType)}</span>`;
        } else if (product.isNew) {
          html += `<span class="new-badge">NEW</span>`;
        }

        html += '</div>';
      }

      html += `<div class="product-info">
        <h4 class="product-title">${escapeHtml(product.title)}</h4>
        <div class="product-price">`;

      if (product.hasReduction && product.oldPriceFormatted) {
        html += `<span class="old-price">${escapeHtml(product.oldPriceFormatted)}</span> `;
      }

      html += `<span class="current-price${product.hasReduction ? ' sale-price' : ''}">${escapeHtml(product.priceFormatted)}</span>
        </div>
      </div>
      </a>
      </div>`;

      return html;
    };

    // Render a single blogpost card
    const renderBlogpostCard = (blogpost) => {
      const image = blogpost.seo_image || blogpost.image;
      // Handle date that could be string or DateTime object serialized from PHP
      let date = blogpost.date || blogpost.publishDate;
      if (date && typeof date === 'object') {
        date = date.date ? date.date.split(' ')[0] : null;
      }

      let html = `<div class="ems-product-card ems-blogpost-card">
        <a href="${escapeHtml(blogpost.url)}" class="product-link">`;

      if (image) {
        html += `<div class="product-image">
          <img src="${escapeHtml(image)}" alt="${escapeHtml(blogpost.title)}" loading="lazy">
        </div>`;
      }

      html += `<div class="product-info">
        <h4 class="product-title">${escapeHtml(blogpost.title)}</h4>`;

      if (date) {
        html += `<div class="blogpost-date">${escapeHtml(date)}</div>`;
      }

      html += `</div>
      </a>
      </div>`;

      return html;
    };

    // Render a single page card
    const renderPageCard = (page) => {
      let html = `<div class="ems-product-card ems-page-card">
        <a href="${escapeHtml(page.url)}" class="product-link">`;

      if (page.image) {
        html += `<div class="product-image">
          <img src="${escapeHtml(page.image)}" alt="${escapeHtml(page.title)}" loading="lazy">
        </div>`;
      }

      html += `<div class="product-info">
        <h4 class="product-title">${escapeHtml(page.title)}</h4>`;

      if (page.description) {
        html += `<div class="page-description">${escapeHtml(page.description)}</div>`;
      }

      html += `</div>
      </a>
      </div>`;

      return html;
    };

    // Bind events
    const bindEvents = () => {
      // Close modal
      modalElement.on('click', '.ems-search-close, .ems-search-modal-overlay-bg', () => {
        hide();
      });

      // Close on Escape key
      $(document).on('keydown.emsSearchModal', (e) => {
        if (e.key === 'Escape' && isOpen) {
          hide();
        }
      });

      // Search input
      searchInput.on('input', () => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(performSearch, options.debounce_ms);
      });

      // Form submit
      modalElement.on('submit', '#ems-search-modal-form', (e) => {
        e.preventDefault();
        performSearch(true);
      });

      // Click on recent search tag
      modalElement.on('click', '.ems-search-tag', function(e) {
        if (!$(e.target).hasClass('remove-tag')) {
          const query = $(this).data('query');
          searchInput.val(query);
          performSearch(true);
        }
      });

      // Remove recent search tag
      modalElement.on('click', '.remove-tag', function(e) {
        e.stopPropagation();
        const query = $(this).data('query');
        SearchHistory.remove(options.site_id, options.module_id, query);
        renderRecentSearches();
      });

      // Clear all history
      modalElement.on('click', '.clear-history', (e) => {
        e.preventDefault();
        SearchHistory.clear(options.site_id, options.module_id);
        renderRecentSearches();
      });

      // Click on popular search
      modalElement.on('click', '.popular-search-link', function(e) {
        e.preventDefault();
        const query = $(this).data('query');
        searchInput.val(query);
        performSearch(true);
      });

      // Click on a product/result link - track the search as successful
      modalElement.on('click', '#ems-search-results a', function() {
        trackCurrentQuery();
      });
    };

    // Track current query to localStorage and server
    const trackCurrentQuery = () => {
      if (!currentQuery || currentQuery.length < options.min_chars) {
        return;
      }

      // Add to localStorage history
      SearchHistory.add(options.site_id, options.module_id, currentQuery);

      // Track on server
      if (options.track_query_url) {
        $.ajax({
          url: options.track_query_url,
          method: 'POST',
          contentType: 'application/json',
          data: JSON.stringify({
            moduleId: options.module_id,
            query: currentQuery,
            lang: options.lang,
          }),
        });
      }
    };

    // Perform search (resets to page 1)
    const performSearch = (trackQuery = false, resetFilters = true) => {
      const query = searchInput.val().trim();

      // Show initial content if query is too short
      if (query.length < options.min_chars) {
        showInitialContent();
        return;
      }

      // Cancel previous request
      if (currentRequest) {
        currentRequest.abort();
      }

      // Reset pagination state for new search
      currentPage = 1;
      totalPages = 1;
      currentQuery = query;

      // Reset filters when query changes (unless triggered by a filter change)
      if (resetFilters) {
        priceFilterMin = null;
        priceFilterMax = null;
        priceRangeInitialized = false;
        selectedCategoryIds = [];
      }

      showLoading();

      const url = new URL(options.search_url, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('page', '1');
      url.searchParams.set('lang', options.lang);

      // Add price filter params (for eco module)
      if (options.module_id === 'eco') {
        if (priceFilterMin !== null && priceRangeMin !== null && priceFilterMin > priceRangeMin) {
          url.searchParams.set('priceMin', priceFilterMin.toString());
        }
        if (priceFilterMax !== null && priceRangeMax !== null && priceFilterMax < priceRangeMax) {
          url.searchParams.set('priceMax', priceFilterMax.toString());
        }
      }

      // Add category filter params (all modules) - multiple categories
      if (selectedCategoryIds.length > 0) {
        url.searchParams.set('categoryIds', selectedCategoryIds.join(','));
      }

      currentRequest = $.ajax({
        url: url.toString(),
        dataType: 'html',
        success: (html) => {
          showSearchResults(html, false);

          // Track query and add to history
          if (trackQuery) {
            trackCurrentQuery();
          }
        },
        error: (xhr, status) => {
          if (status !== 'abort') {
            showError();
          }
        },
      });
    };

    // Load more results (infinite scroll)
    const loadMoreResults = () => {
      if (isLoadingMore || currentPage >= totalPages || !currentQuery) {
        return;
      }

      isLoadingMore = true;
      const nextPage = currentPage + 1;

      // Show loading indicator at bottom
      const loadingIndicator = $('<div class="ems-search-loading-more"><div class="spinner"></div></div>');
      $('#ems-search-results').append(loadingIndicator);

      const url = new URL(options.search_url, window.location.origin);
      url.searchParams.set('q', currentQuery);
      url.searchParams.set('page', nextPage.toString());
      url.searchParams.set('lang', options.lang);

      // Add price filter params (for eco module)
      if (options.module_id === 'eco') {
        if (priceFilterMin !== null && priceRangeMin !== null && priceFilterMin > priceRangeMin) {
          url.searchParams.set('priceMin', priceFilterMin.toString());
        }
        if (priceFilterMax !== null && priceRangeMax !== null && priceFilterMax < priceRangeMax) {
          url.searchParams.set('priceMax', priceFilterMax.toString());
        }
      }

      // Add category filter params (all modules) - multiple categories
      if (selectedCategoryIds.length > 0) {
        url.searchParams.set('categoryIds', selectedCategoryIds.join(','));
      }

      $.ajax({
        url: url.toString(),
        dataType: 'html',
        success: (html) => {
          loadingIndicator.remove();
          showSearchResults(html, true);
          currentPage = nextPage;
          isLoadingMore = false;
        },
        error: () => {
          loadingIndicator.remove();
          isLoadingMore = false;
        },
      });
    };

    // Show initial content (before typing)
    const showInitialContent = () => {
      // Reset infinite scroll state
      currentPage = 1;
      totalPages = 1;
      currentQuery = '';
      isLoadingMore = false;

      // Reset price filter state
      priceFilterMin = null;
      priceFilterMax = null;
      priceRangeMin = null;
      priceRangeMax = null;
      priceRangeInitialized = false;

      // Reset category filter state
      selectedCategoryIds = [];
      categoryFacets = [];

      if (infiniteScrollObserver) {
        infiniteScrollObserver.disconnect();
        infiniteScrollObserver = null;
      }

      $('#ems-search-initial').show();
      $('#ems-search-results-wrapper').hide();
      $('#ems-search-results').empty();
      $('#ems-search-filters').empty();
      $('#ems-search-loading').hide();
      renderRecentSearches();
    };

    // Show loading state
    const showLoading = () => {
      $('#ems-search-initial').hide();
      $('#ems-search-results-wrapper').hide();
      $('#ems-search-loading').show();
    };

    // Show search results
    const showSearchResults = (html, append = false) => {
      $('#ems-search-initial').hide();
      $('#ems-search-loading').hide();

      const resultsWrapper = $('#ems-search-results-wrapper');
      const resultsContainer = $('#ems-search-results');

      if (!html || html.trim() === '') {
        if (!append) {
          resultsContainer.html(`<div class="ems-search-no-results">${options.labels.no_results}</div>`);
          $('#ems-search-filters').empty();
        }
        resultsWrapper.show();
        return;
      }

      // Parse the HTML to extract pagination info, items, and price stats
      const $html = $('<div>').html(html);
      const $pagination = $html.find('.ems-search-pagination');
      const $items = $html.find('.eco-item, .blog-item, .page-item-result, .agenda-item');
      const $emsSearch = $html.find('#ems_search');

      // Extract price stats from data attribute (for eco module)
      let priceStats = null;
      if ($emsSearch.length && $emsSearch.data('price-stats')) {
        priceStats = $emsSearch.data('price-stats');
      }

      // Extract category facets from data attribute (all modules)
      if ($emsSearch.length && $emsSearch.data('category-facets')) {
        categoryFacets = $emsSearch.data('category-facets');
      } else {
        categoryFacets = [];
      }

      // Extract pagination data
      if ($pagination.length) {
        const $lastPageLink = $pagination.find('.page-link[data-page]').last();

        if ($lastPageLink.length) {
          totalPages = parseInt($lastPageLink.data('page'), 10) || 1;
        }

        // Remove pagination from HTML (we use infinite scroll instead)
        $pagination.remove();
      }

      if (append) {
        // Append new items to existing container
        const $existingContainer = resultsContainer.find('#ems_search');
        if ($existingContainer.length && $items.length) {
          $existingContainer.append($items);
        }
      } else {
        // Replace content entirely
        resultsContainer.html($html.html());

        // Render filters (first search only, not append)
        renderFilters(priceStats);
      }

      resultsWrapper.show();

      // Setup infinite scroll observer
      setupInfiniteScroll();
    };

    // Setup IntersectionObserver for infinite scroll
    const setupInfiniteScroll = () => {
      // Disconnect previous observer
      if (infiniteScrollObserver) {
        infiniteScrollObserver.disconnect();
      }

      // Don't setup if we're on the last page
      if (currentPage >= totalPages) {
        return;
      }

      // Create sentinel element if not exists
      let sentinel = document.getElementById('ems-search-sentinel');
      if (!sentinel) {
        sentinel = document.createElement('div');
        sentinel.id = 'ems-search-sentinel';
        sentinel.className = 'ems-search-sentinel';
        $('#ems-search-results').append(sentinel);
      }

      // Create observer
      infiniteScrollObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !isLoadingMore && currentPage < totalPages) {
              loadMoreResults();
            }
          });
        },
        {
          root: document.querySelector('.ems-search-modal-body'),
          rootMargin: '200px',
          threshold: 0,
        },
      );

      infiniteScrollObserver.observe(sentinel);
    };

    // Render all filters (category + price for eco)
    const renderFilters = (priceStats) => {
      const filtersContainer = $('#ems-search-filters');
      let filtersHtml = '';

      // Category filter (all modules)
      filtersHtml += renderCategoryFilter();

      // Price filter (eco module only)
      if (options.module_id === 'eco' && priceStats) {
        filtersHtml += renderPriceFilter(priceStats);
      }

      filtersContainer.html(filtersHtml);

      // Bind events after rendering
      if (options.module_id === 'eco' && priceStats) {
        updatePriceSliderTrack();
        bindPriceSliderEvents();
      }
      bindCategoryFilterEvents();
    };

    // Render category filter (all modules) - uses categories from search results
    const renderCategoryFilter = () => {
      if (!categoryFacets || !categoryFacets.length) {
        return '';
      }

      // Build checkboxes HTML
      let checkboxesHtml = '';

      categoryFacets.forEach((cat) => {
        const checked = selectedCategoryIds.includes(cat.id) ? 'checked' : '';
        checkboxesHtml += `
          <label class="ems-category-checkbox">
            <input type="checkbox" name="ems-category" value="${cat.id}" ${checked}>
            <span class="category-name">${escapeHtml(cat.name)}</span>
            <span class="category-count">(${cat.count})</span>
          </label>
        `;
      });

      return `
        <div class="ems-filter-section ems-category-filter-section">
          <div class="ems-filter-header">
            <span class="filter-title">${escapeHtml(options.labels.category)}</span>
          </div>
          <div class="ems-category-filter">
            ${checkboxesHtml}
          </div>
        </div>
      `;
    };

    // Bind category filter events
    const bindCategoryFilterEvents = () => {
      const checkboxes = document.querySelectorAll('input[name="ems-category"]');
      if (!checkboxes.length) return;

      checkboxes.forEach((checkbox) => {
        checkbox.addEventListener('change', () => {
          const categoryId = checkbox.value; // Keep as string (UUIDs)

          if (checkbox.checked) {
            // Add to selection if not already present
            if (!selectedCategoryIds.includes(categoryId)) {
              selectedCategoryIds.push(categoryId);
            }
          } else {
            // Remove from selection
            selectedCategoryIds = selectedCategoryIds.filter((id) => id !== categoryId);
          }

          // Re-run search with new category filter
          performSearch(false, false);
        });
      });
    };

    // Render price filter (for eco module)
    const renderPriceFilter = (stats) => {
      // Only show for eco module
      if (options.module_id !== 'eco' || !stats || typeof stats.min === 'undefined') {
        return '';
      }

      // Only set the range from the FIRST search (without filters)
      // This keeps the slider range fixed like Doofinder
      if (!priceRangeInitialized) {
        priceRangeMin = Math.floor(stats.min);
        priceRangeMax = Math.ceil(stats.max);
        priceRangeInitialized = true;
      }

      // Don't show filter if range is too small
      if (priceRangeMin >= priceRangeMax) {
        return '';
      }

      // Initialize filter values to full range if not set
      if (priceFilterMin === null) priceFilterMin = priceRangeMin;
      if (priceFilterMax === null) priceFilterMax = priceRangeMax;

      return `
        <div class="ems-filter-section ems-price-filter-section">
          <div class="ems-filter-header">
            <span class="filter-title">${escapeHtml(options.labels.price)}</span>
          </div>
          <div class="ems-price-filter">
            <div class="price-values">
              <span class="price-badge min" id="ems-price-min-display">${priceFilterMin} €</span>
              <span class="price-badge max" id="ems-price-max-display">${priceFilterMax} €</span>
            </div>
            <div class="price-slider-container">
              <div class="slider-track" id="ems-price-slider-track"></div>
              <input type="range" id="ems-price-min-slider"
                     min="${priceRangeMin}" max="${priceRangeMax}"
                     value="${priceFilterMin}" step="1">
              <input type="range" id="ems-price-max-slider"
                     min="${priceRangeMin}" max="${priceRangeMax}"
                     value="${priceFilterMax}" step="1">
            </div>
            <div class="price-range-labels">
              <span>${priceRangeMin} €</span>
              <span>${priceRangeMax} €</span>
            </div>
          </div>
        </div>
      `;
    };

    // Update price slider track gradient
    // Uses CSS variables (--primary, --light) for consistency with facet filters
    const updatePriceSliderTrack = () => {
      if (priceRangeMin === null || priceRangeMax === null) return;

      const track = document.getElementById('ems-price-slider-track');
      if (!track) return;

      const range = priceRangeMax - priceRangeMin;
      if (range <= 0) return;

      const minPercent = ((priceFilterMin - priceRangeMin) / range) * 100;
      const maxPercent = ((priceFilterMax - priceRangeMin) / range) * 100;

      track.style.background = `linear-gradient(
        to right,
        var(--light, #ddd) 0%,
        var(--light, #ddd) ${minPercent}%,
        var(--primary, #007bff) ${minPercent}%,
        var(--primary, #007bff) ${maxPercent}%,
        var(--light, #ddd) ${maxPercent}%,
        var(--light, #ddd) 100%
      )`;
    };

    // Bind price slider events
    const bindPriceSliderEvents = () => {
      const minSlider = document.getElementById('ems-price-min-slider');
      const maxSlider = document.getElementById('ems-price-max-slider');
      const minDisplay = document.getElementById('ems-price-min-display');
      const maxDisplay = document.getElementById('ems-price-max-display');

      if (!minSlider || !maxSlider) return;

      const handleMinChange = () => {
        let minVal = parseInt(minSlider.value, 10);
        const maxVal = parseInt(maxSlider.value, 10);

        // Prevent min from exceeding max
        if (minVal >= maxVal) {
          minVal = maxVal - 1;
          minSlider.value = minVal;
        }

        priceFilterMin = minVal;
        if (minDisplay) minDisplay.textContent = minVal + ' €';
        updatePriceSliderTrack();
        triggerPriceFilterSearch();
      };

      const handleMaxChange = () => {
        const minVal = parseInt(minSlider.value, 10);
        let maxVal = parseInt(maxSlider.value, 10);

        // Prevent max from going below min
        if (maxVal <= minVal) {
          maxVal = minVal + 1;
          maxSlider.value = maxVal;
        }

        priceFilterMax = maxVal;
        if (maxDisplay) maxDisplay.textContent = maxVal + ' €';
        updatePriceSliderTrack();
        triggerPriceFilterSearch();
      };

      minSlider.addEventListener('input', handleMinChange);
      maxSlider.addEventListener('input', handleMaxChange);
    };

    // Trigger search after price filter change (debounced)
    const triggerPriceFilterSearch = () => {
      clearTimeout(priceFilterDebounce);
      priceFilterDebounce = setTimeout(() => {
        // Re-run search with new price filter (don't reset the filter)
        performSearch(false, false);
      }, 500);
    };

    // Show error state
    const showError = () => {
      $('#ems-search-initial').hide();
      $('#ems-search-loading').hide();
      $('#ems-search-results-wrapper').show();
      $('#ems-search-results').html('<div class="ems-search-error">Erreur lors de la recherche</div>');
      $('#ems-search-filters').empty();
    };

    // Show modal
    const show = () => {
      isOpen = true;
      modalElement.addClass('is-open');
      $('body').addClass('ems-search-modal-open');

      // Focus input after animation
      setTimeout(() => {
        searchInput.focus();
      }, 100);

      // Fetch modal data on first open (lazy loading)
      if (!modalDataLoaded && options.modal_data_url) {
        modalDataLoaded = true;
        fetchModalData();
      } else {
        // Just refresh recent searches from localStorage
        renderRecentSearches();
      }
    };

    // Hide modal
    const hide = () => {
      isOpen = false;
      modalElement.removeClass('is-open');
      $('body').removeClass('ems-search-modal-open');

      // Reset state
      searchInput.val('');
      showInitialContent();
    };

    // Escape HTML, attributes included (textContent/innerHTML leaves quotes unescaped)
    const escapeHtml = (str) => {
      if (str === null || str === undefined || str === '') return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    };

    // Initialize
    init();

    // Public API
    window.EmsSearchModal = {
      show,
      hide,
      isOpen: () => isOpen,
    };
  };
})(jQuery);
